import { Component } from "@/types"
import { User } from "@/schemas/User"
import { cx } from "@/utils/cx"
import { useSearchParams } from "next/navigation"

import { FormLinksWrapper } from "@/ui/atoms/form/FormLinksWrapper"
import { NotificationCard } from "@/ui/blocks/notifications/NotificationCard"
import { SplashCard } from "@/ui/atoms/layout/SplashCard"
import { Stack } from "@/ui/atoms/layout/Stack"
import { TextLink } from "@/ui/atoms/controls/TextLink"
import { Title } from "@/ui/atoms/type/Title"
import { SANSSignInButton } from "./_elements/SANSSignInButton"
import { BodyText } from "@/ui/atoms/type/BodyText"
import { SUPPORT_CONTACT_LINK, SUPPORT_EMAIL_MAIL_TO } from "@/constants"
import { tx } from "@/utils/tx"

export const COMPONENT_NAME = "signInForm"

export const presets = {}

export interface Props extends Component {
  onSuccess: (newCurrentUser: User, redirectTo?: string) => void
}

export function SignInForm({ onSuccess, className, testId = COMPONENT_NAME }: Props) {
  const searchParams = useSearchParams()
  const reason = searchParams.get("r")
  const error = searchParams.get("error")

  return (
    <SplashCard
      className={cx(`w-full max-w-screen-form flex flex-col gap-8`, className)}
      testId={testId}
    >
      <header>
        <Stack className="gap-10 md:gap-8">
          <Title size="lg" className="text-center">
            Sign in
          </Title>

          {/* Covers all generic sso errors (including unknown_error) */}
          {error?.startsWith("sso_") && (
            <NotificationCard
              variant="warning"
              title="Something went wrong"
              text="Please try signing in again. If the issue persists, please contact us."
              links={[SUPPORT_CONTACT_LINK]}
              className="border border-border-form border-l-0"
            />
          )}

          {error === "account_suspended" && (
            <NotificationCard
              variant="warning"
              className="border border-border-form border-l-0"
              title="Account suspended"
              text="The account associated with this email has been suspended."
              links={[
                {
                  label: "Please contact us for assistance",
                  href: SUPPORT_EMAIL_MAIL_TO,
                },
              ]}
            />
          )}

          {error === "server_error" && (
            <NotificationCard
              variant="warning"
              title="Something went wrong"
              text="Please try signing in again. If the issue persists, please contact us."
              links={[SUPPORT_CONTACT_LINK]}
              className="border border-border-form border-l-0"
            />
          )}

          {reason === "NOT_LOGGED_IN" && (
            <NotificationCard
              variant="warning"
              title="Sign in to continue"
              text="You need to be signed in to access this page"
              links={[]}
              className="border border-border-form border-l-0"
            />
          )}

          {reason === "SESSION_EXPIRED" && (
            <NotificationCard
              variant="warning"
              title="Sign in to continue"
              text="Your session has expired, please sign in again to continue"
              links={[]}
              className="border border-border-form border-l-0"
            />
          )}
        </Stack>
      </header>
      <main className="flex flex-col gap-8">
        <SANSSignInButton />
      </main>
      <footer className="flex flex-col gap-4">
        <BodyText size="sm" className="text-center">
          If you created your account before September 25, 2025 you can also still sign in with your
          email and password.
        </BodyText>
        <FormLinksWrapper>
          <TextLink href="/legacy/sign-in" testId={tx(testId, "legacySignInLink")}>
            Sign in with email and password (legacy)
          </TextLink>

          {/* <TextLink href="/sign-in/more-info" testId="moreInfoLink" style="bold">
              More info
            </TextLink> */}
        </FormLinksWrapper>
      </footer>
    </SplashCard>
  )
}
